import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import { Box, Typography, Chip, Stack, Tooltip } from '@mui/material'
import Iconify from 'src/components/iconify'
import { masks } from 'src/utils/masks'

// ----------------------------------------------------------------------

type PaymentType = 'money' | 'credit' | 'debit' | 'pix' | 'card'

interface PaymentDetail {
  type: PaymentType
  quantity: number
  value: number
}

interface PaymentTypeInfo {
  label: string
  icon: string
  color: string
}

type Props = {
  selected: boolean
  row: any
}

const PAYMENT_TYPE_LABELS: Record<PaymentType, PaymentTypeInfo> = {
  money: { label: 'Dinheiro', icon: 'solar:banknote-2-bold', color: '#2E7D32' },
  credit: { label: 'Crédito', icon: 'solar:card-send-bold', color: '#1976D2' },
  debit: { label: 'Débito', icon: 'solar:card-2-bold-duotone', color: '#ED6C02' },
  pix: { label: 'PIX', icon: 'solar:qr-code-bold', color: '#9C27B0' },
  card: { label: 'Cashless', icon: 'solar:wallet-money-bold', color: '#D32F2F' },
}

interface PriceBreakdownItem {
  price: number
  quantity: number
  value: number
}

// ----------------------------------------------------------------------

const PriceBreakdownTooltip = ({
  priceBreakdown,
  currentPrice,
}: {
  priceBreakdown: PriceBreakdownItem[]
  currentPrice: number
}) => {
  const totalQty = priceBreakdown.reduce((acc, p) => acc + (p.quantity || 0), 0)
  const totalValue = priceBreakdown.reduce((acc, p) => acc + (p.value || 0), 0)

  return (
    <Box
      sx={{
        minWidth: 300,
        bgcolor: 'background.paper',
        borderRadius: 1.5,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Iconify icon='solar:tag-price-bold' width={16} sx={{ color: 'warning.main' }} />
        <Typography variant='caption' fontWeight={700} color='text.primary' sx={{ flex: 1 }}>
          Variação de preço durante o evento
        </Typography>
        <Chip
          label={`${priceBreakdown.length} preço${priceBreakdown.length === 1 ? '' : 's'}`}
          size='small'
          color='warning'
          variant='soft'
          sx={{ height: 18, fontSize: 10, fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
        />
      </Stack>

      <Box>
        {priceBreakdown.map((item, index) => {
          const pct = totalQty ? Math.round(((item.quantity || 0) / totalQty) * 100) : 0
          const isCurrent = item.price === currentPrice
          const isLast = index === priceBreakdown.length - 1

          return (
            <Stack
              key={index}
              direction='row'
              alignItems='center'
              spacing={1.25}
              sx={{
                px: 1.5,
                py: 0.875,
                borderBottom: isLast ? 'none' : '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: isCurrent ? 'success.lighter' : 'grey.100',
                  color: isCurrent ? 'success.dark' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon='solar:tag-price-bold' width={14} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction='row' alignItems='center' spacing={0.5}>
                  <Typography
                    variant='body2'
                    fontWeight={700}
                    color='text.primary'
                    sx={{ fontSize: 13, lineHeight: 1.3 }}
                  >
                    {masks.currencyAllPlatforms(item.price || 0)}
                  </Typography>
                  {isCurrent && (
                    <Chip
                      label='Atual'
                      size='small'
                      color='success'
                      variant='soft'
                      sx={{
                        height: 16,
                        fontSize: 9,
                        fontWeight: 700,
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}
                </Stack>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontSize: 11, lineHeight: 1.2 }}
                >
                  {item.quantity || 0} unid. · {pct}% do total
                </Typography>
              </Box>

              <Typography
                variant='body2'
                fontWeight={700}
                color='success.dark'
                sx={{ fontSize: 13, whiteSpace: 'nowrap' }}
              >
                {masks.currencyAllPlatforms(item.value || 0)}
              </Typography>
            </Stack>
          )
        })}
      </Box>

      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'success.lighter',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant='caption' fontWeight={700} color='text.primary' sx={{ fontSize: 12 }}>
          TOTAL ({totalQty} unid.)
        </Typography>
        <Typography
          variant='body2'
          fontWeight={700}
          color='success.darker'
          sx={{ fontSize: 13.5 }}
        >
          {masks.currencyAllPlatforms(totalValue)}
        </Typography>
      </Stack>
    </Box>
  )
}

// ----------------------------------------------------------------------

const PaymentDetailsTooltip = ({ paymentDetails }: { paymentDetails: PaymentDetail[] }) => {
  if (!paymentDetails || paymentDetails.length === 0) {
    return (
      <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant='caption' color='text.secondary'>
          Nenhum dado de pagamento disponível
        </Typography>
      </Box>
    )
  }

  const totalValue = paymentDetails.reduce((acc, p) => acc + (p.value || 0), 0)
  const totalQty = paymentDetails.reduce((acc, p) => acc + (p.quantity || 0), 0)

  return (
    <Box
      sx={{
        minWidth: 320,
        bgcolor: 'background.paper',
        borderRadius: 1.5,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Iconify icon='solar:card-bold' width={16} sx={{ color: 'primary.main' }} />
        <Typography variant='caption' fontWeight={700} color='text.primary' sx={{ flex: 1 }}>
          Detalhes por tipo de pagamento
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
          {totalQty} unid.
        </Typography>
      </Stack>

      {/* Linhas */}
      <Box>
        {paymentDetails.map((payment, index) => {
          const info = PAYMENT_TYPE_LABELS[payment.type] || {
            label: payment.type || 'Outro',
            icon: 'solar:card-bold',
            color: '#757575',
          }

          const pct = totalValue ? Math.round(((payment.value || 0) / totalValue) * 100) : 0
          const isLast = index === paymentDetails.length - 1

          return (
            <Stack
              key={index}
              direction='row'
              alignItems='center'
              spacing={1.25}
              sx={{
                px: 1.5,
                py: 0.875,
                borderBottom: isLast ? 'none' : '1px dashed',
                borderColor: 'divider',
              }}
            >
              {/* Ícone */}
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: `${info.color}15`,
                  color: info.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon={info.icon} width={16} />
              </Box>

              {/* Coluna central: label + quantidade */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  color='text.primary'
                  sx={{ fontSize: 13, lineHeight: 1.3 }}
                >
                  {info.label}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontSize: 11, lineHeight: 1.2 }}
                >
                  {payment.quantity || 0} unid.
                </Typography>
              </Box>

              {/* Coluna direita: valor + porcentagem */}
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography
                  variant='body2'
                  fontWeight={700}
                  sx={{
                    color: info.color,
                    fontSize: 13,
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {masks.currencyAllPlatforms(payment.value || 0)}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontSize: 11, lineHeight: 1.2 }}
                >
                  {pct}% do total
                </Typography>
              </Box>
            </Stack>
          )
        })}
      </Box>

      {/* Total */}
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'success.lighter',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant='caption' fontWeight={700} color='text.primary' sx={{ fontSize: 12 }}>
          TOTAL
        </Typography>
        <Typography
          variant='body2'
          fontWeight={700}
          color='success.darker'
          sx={{ fontSize: 13.5 }}
        >
          {masks.currencyAllPlatforms(totalValue)}
        </Typography>
      </Stack>
    </Box>
  )
}

// ----------------------------------------------------------------------

export default function ReportsTableRow({
  row,
  selected,
}: Props) {
  const {
    name,
    stock,
    price,
    cost,
    totalSale,
    saida,
    stockCost,
    paymentDetails,
    priceBreakdown,
    active,
    category,
  } = row
  const isInactive = active === false
  const hasPriceVariation = Array.isArray(priceBreakdown) && priceBreakdown.length > 1

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        '& td': { py: 1, borderBottom: '1px solid', borderColor: 'divider', fontSize: 13 },
        '&:hover': { bgcolor: 'action.hover' },
        ...(isInactive && {
          bgcolor: 'rgba(244, 67, 54, 0.04)',
          '& td': {
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontSize: 13,
            color: 'text.disabled',
          },
        }),
      }}
    >
      {/* Produto */}
      <TableCell>
        <Stack direction='row' alignItems='center' spacing={1}>
          <Iconify
            icon={isInactive ? 'mdi:package-variant-closed-remove' : 'mdi:package-variant'}
            width={16}
            sx={{ color: isInactive ? 'error.main' : 'primary.main', flexShrink: 0 }}
          />
          <Typography
            variant='body2'
            fontWeight={600}
            sx={{
              fontSize: 13,
              color: isInactive ? 'text.disabled' : 'text.primary',
              textDecoration: isInactive ? 'line-through' : 'none',
            }}
            noWrap
          >
            {name}
          </Typography>
          {isInactive && (
            <Chip
              label='Desativado'
              size='small'
              color='error'
              variant='soft'
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Stack>
      </TableCell>

      {/* Categoria */}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {category?.name ? (
          <Chip
            size='small'
            label={category.name}
            variant='soft'
            color='info'
            icon={<Iconify icon='solar:tag-bold' width={12} />}
            sx={{
              fontWeight: 600,
              fontSize: 11,
              height: 22,
              '& .MuiChip-label': { px: 0.75 },
              '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
              ...(isInactive && { opacity: 0.5 }),
            }}
          />
        ) : (
          <Typography variant='caption' color='text.disabled' sx={{ fontStyle: 'italic' }}>
            Sem categoria
          </Typography>
        )}
      </TableCell>

      {/* Estoque */}
      <TableCell align='center' sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13 }}>
          {stock}
        </Typography>
      </TableCell>

      {/* Vendido */}
      <TableCell align='center' sx={{ whiteSpace: 'nowrap' }}>
        <Chip
          label={saida || 0}
          size='small'
          color='success'
          variant='soft'
          sx={{ fontWeight: 700, height: 22, minWidth: 44, fontSize: 12 }}
        />
      </TableCell>

      {/* Preço Unitário */}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {hasPriceVariation ? (
          <Tooltip
            title={
              <PriceBreakdownTooltip
                priceBreakdown={priceBreakdown}
                currentPrice={price}
              />
            }
            arrow
            placement='top'
            enterDelay={250}
            leaveDelay={150}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'transparent',
                  p: 0,
                  m: 0,
                  maxWidth: 'none',
                  '&.MuiTooltip-tooltip': { bgcolor: 'transparent', p: 0, m: 0 },
                },
              },
              arrow: { sx: { color: 'background.paper' } },
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              spacing={0.5}
              sx={{
                cursor: 'help',
                display: 'inline-flex',
                '&:hover .variation-icon': { opacity: 1 },
              }}
            >
              <Typography variant='body2' sx={{ fontSize: 13 }}>
                {masks.currencyAllPlatforms(price)}
              </Typography>
              <Chip
                label={`${priceBreakdown.length}x`}
                size='small'
                color='warning'
                variant='soft'
                icon={
                  <Iconify
                    icon='solar:tag-price-bold'
                    width={11}
                    className='variation-icon'
                    sx={{ opacity: 0.8, transition: 'opacity 0.15s' }}
                  />
                }
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.5 },
                  '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
                }}
              />
            </Stack>
          </Tooltip>
        ) : (
          <Typography variant='body2' sx={{ fontSize: 13 }}>
            {masks.currencyAllPlatforms(price)}
          </Typography>
        )}
      </TableCell>

      {/* Faturamento Total */}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Tooltip
          title={<PaymentDetailsTooltip paymentDetails={paymentDetails || []} />}
          arrow
          placement='top'
          enterDelay={250}
          leaveDelay={150}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'transparent',
                p: 0,
                m: 0,
                maxWidth: 'none',
                '&.MuiTooltip-tooltip': { bgcolor: 'transparent', p: 0, m: 0 },
              },
            },
            arrow: { sx: { color: 'background.paper' } },
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            spacing={0.5}
            sx={{
              cursor: 'help',
              display: 'inline-flex',
              '&:hover .info-icon': { opacity: 1 },
            }}
          >
            <Typography
              variant='body2'
              fontWeight={700}
              color='success.dark'
              sx={{ fontSize: 13 }}
            >
              {masks.currencyAllPlatforms(totalSale)}
            </Typography>
            <Iconify
              icon='mdi:information-outline'
              width={13}
              className='info-icon'
              sx={{ color: 'success.main', opacity: 0.5, transition: 'opacity 0.15s' }}
            />
          </Stack>
        </Tooltip>
      </TableCell>

      {/* Custo Unitário */}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant='body2' color='text.secondary' sx={{ fontSize: 13 }}>
          {masks.currencyAllPlatforms(cost)}
        </Typography>
      </TableCell>

      {/* Valor do Estoque */}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant='body2' fontWeight={600} color='warning.dark' sx={{ fontSize: 13 }}>
          {masks.currencyAllPlatforms(stockCost)}
        </Typography>
      </TableCell>
    </TableRow>
  )
}
